import { useState, useEffect, useRef } from "react";

export interface Track {
  name: string;
  url: string;
}

export function useMusicPlayer() {
  const audioRef      = useRef<HTMLAudioElement | null>(null);
  const fileInputRef  = useRef<HTMLInputElement>(null);
  const urlsRef       = useRef<string[]>([]);

  // Refs that event-handler closures (attached once) can read without going stale
  const tracksRef     = useRef<Track[]>([]);
  const idxRef        = useRef(0);
  const repeatRef     = useRef<"none" | "all" | "one">("none");
  const shuffleRef    = useRef(false);

  const [tracks,      setTracksState]  = useState<Track[]>([]);
  const [currentIdx,  setIdxState]     = useState(0);
  const [isPlaying,   setIsPlaying]    = useState(false);
  const [currentTime, setCurrentTime]  = useState(0);
  const [duration,    setDuration]     = useState(0);
  const [volume,      setVolumeState]  = useState(1);
  const [muted,       setMuted]        = useState(false);
  const [shuffle,     setShuffleState] = useState(false);
  const [repeat,      setRepeatState]  = useState<"none" | "all" | "one">("none");
  const [showPlaylist, setShowPlaylist] = useState(false);

  // Helpers that keep state + ref in sync
  const setTracks = (t: Track[]) => { tracksRef.current = t; setTracksState(t); };
  const setIdx    = (i: number)  => { idxRef.current = i;    setIdxState(i); };
  const setRepeat = (r: "none" | "all" | "one") => { repeatRef.current = r; setRepeatState(r); };
  const setShuffle = (s: boolean) => { shuffleRef.current = s; setShuffleState(s); };

  // ── Init audio element once ──────────────────────────────────────────────────
  useEffect(() => {
    const audio = new Audio();
    audioRef.current = audio;

    audio.addEventListener("timeupdate",      () => setCurrentTime(audio.currentTime));
    audio.addEventListener("durationchange",  () => setDuration(isFinite(audio.duration) ? audio.duration : 0));
    audio.addEventListener("play",            () => setIsPlaying(true));
    audio.addEventListener("pause",           () => setIsPlaying(false));
    audio.addEventListener("ended", () => {
      const len = tracksRef.current.length;
      if (!len) return;
      if (repeatRef.current === "one") { audio.play().catch(() => {}); return; }
      const cur = idxRef.current;
      let next: number;
      if (shuffleRef.current) {
        next = Math.floor(Math.random() * len);
        if (len > 1 && next === cur) next = (next + 1) % len;
      } else {
        next = cur + 1;
        if (next >= len) {
          if (repeatRef.current === "all") next = 0;
          else { setIsPlaying(false); return; }
        }
      }
      const track = tracksRef.current[next];
      if (track) { audio.src = track.url; audio.play().catch(() => {}); }
      setIdx(next);
    });

    return () => {
      audio.pause();
      audio.src = "";
      urlsRef.current.forEach(u => URL.revokeObjectURL(u));
    };
  }, []);

  // ── Public actions ───────────────────────────────────────────────────────────

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (!files.length) return;
    urlsRef.current.forEach(u => URL.revokeObjectURL(u));
    const urls = files.map(f => URL.createObjectURL(f));
    urlsRef.current = urls;
    const newTracks = files.map((f, i): Track => ({
      name: f.name.replace(/\.[^/.]+$/, ""),
      url: urls[i],
    }));
    setTracks(newTracks);
    setIdx(0);
    setCurrentTime(0);
    setDuration(0);
    setIsPlaying(false);
    if (audioRef.current) audioRef.current.src = "";
    e.target.value = "";
    setShowPlaylist(true);
  };

  const goTo = (idx: number) => {
    const audio = audioRef.current;
    const track = tracksRef.current[idx];
    if (!audio || !track) return;
    setIdx(idx);
    audio.src = track.url;
    audio.load();
    audio.play().catch(() => {});
  };

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio || !tracksRef.current.length) return;
    if (!audio.paused) audio.pause();
    else {
      // If no src yet, load first track
      if (!audio.src && tracksRef.current[0]) audio.src = tracksRef.current[0].url;
      audio.play().catch(() => {});
    }
  };

  const goPrev = () => {
    const audio = audioRef.current;
    if (!tracksRef.current.length) return;
    if (audio && audio.currentTime > 3) { audio.currentTime = 0; return; }
    goTo(idxRef.current - 1 < 0 ? tracksRef.current.length - 1 : idxRef.current - 1);
  };

  const goNext = () => {
    if (!tracksRef.current.length) return;
    const len = tracksRef.current.length;
    const next = shuffleRef.current
      ? (len > 1 ? (idxRef.current + 1 + Math.floor(Math.random() * (len - 1))) % len : 0)
      : (idxRef.current + 1) % len;
    goTo(next);
  };

  const handleSeek = (val: number) => {
    if (audioRef.current) audioRef.current.currentTime = val;
    setCurrentTime(val);
  };

  const handleVolume = (val: number) => {
    setVolumeState(val);
    if (audioRef.current) { audioRef.current.volume = val; audioRef.current.muted = false; }
    setMuted(val === 0);
  };

  const toggleMute = () => {
    if (!audioRef.current) return;
    const next = !muted;
    audioRef.current.muted = next;
    setMuted(next);
  };

  const cycleRepeat = () =>
    setRepeat(repeat === "none" ? "all" : repeat === "all" ? "one" : "none");

  const removeTrack = (idx: number) => {
    URL.revokeObjectURL(urlsRef.current[idx]);
    urlsRef.current.splice(idx, 1);
    const next = tracks.filter((_, i) => i !== idx);
    setTracks(next);
    if (idx === idxRef.current) {
      audioRef.current?.pause();
      if (audioRef.current) audioRef.current.src = "";
      setIsPlaying(false);
      setIdx(Math.min(idx, next.length - 1));
    } else if (idx < idxRef.current) {
      setIdx(idxRef.current - 1);
    }
  };

  return {
    tracks, currentIdx, isPlaying, currentTime, duration,
    volume, muted, shuffle, repeat,
    showPlaylist, setShowPlaylist, fileInputRef,
    current: tracks[currentIdx] ?? null,
    togglePlay, goTo, goPrev, goNext,
    handleSeek, handleVolume, toggleMute,
    cycleRepeat, setShuffle, handleFileChange, removeTrack,
  };
}

export type MusicPlayerState = ReturnType<typeof useMusicPlayer>;
